import { getAdDeviceType } from '../../../client'
import { AdS, IItem, IPageMeta, ITag } from '../../../types'
import { getParameterByName, prettyLog } from '../../../utils'
import { ILevels } from '../Models/PlayerModels'
import { playerConfig } from './constants'
import { utils } from './utils'
import {
  fetchAndParseVast,
  fetchVastWithDetails,
  fireVastError,
  getClickThroughUrl,
  getLinearCreative,
  VastLinearCreative,
  type VastParsedAd,
} from './vastReels'
// Inside dfpUtils.ts
const isMobile = () => {
  if (typeof window === 'undefined') {
    return false
  }
  return /Mobi|Android|iPhone/i.test(navigator.userAgent)
}

/*{
    "id": "GDFP",
    "fileURL": "https://cdnapisec.kaltura.com/p/2748741/sp/274874100/playManifest/entryId/1_lsd1zee2/format/url/protocol/https/flavorParamIds/487041/sname/2SecClip.mp4",
    "fileSize": 0,
    "deliveryType": "progressive",
    "mimeType": "video/mp4",
    "mediaType": "2D",
    "codec": null,
    "bitrate": 458,
    "minBitrate": 0,
    "maxBitrate": 0,
    "width": 360,
    "height": 640,
    "apiFramework": null,
    "scalable": true,
    "maintainAspectRatio": true
}*/

export function pickBestMediaFile(
  mediaFiles?: VastLinearCreative['mediaFiles']
): {
  fileURL?: string
  mimeType?: string
  height?: number
} | null {
  if (!mediaFiles || mediaFiles.length === 0) {
    return null
  }

  // Set targets: 500kbps for mobile, higher (720p equivalent ~2000kbps) for desktop
  const TARGET_BITRATE = isMobile() ? 800 : 2000

  return mediaFiles.reduce((prev, curr) => {
    const currDiff = Math.abs((curr.bitrate || 0) - TARGET_BITRATE)
    const prevDiff = Math.abs((prev.bitrate || 0) - TARGET_BITRATE)

    // If the VAST asset explicitly provides height, we can target 720 directly on desktop
    if (!isMobile()) {
      // If one of the files is exactly 720p, prefer it
      if (curr.height === 720) {
        return curr
      }
      if (prev.height === 720) {
        return prev
      }
    }

    return currDiff < prevDiff ? curr : prev
  })
}
export interface ICustParams {
  dxu: string
  dxseg: string
  platform: 'desktop' | 'mobile' | 'webview_app'
  type_of_page: IPageMeta['pageType'] | string
  level1: IPageMeta['levels']['level_1_eng']
  level2: IPageMeta['levels']['level_2_eng']
  level3: IPageMeta['levels']['level_3_eng']
  level4: IPageMeta['levels']['level_4_eng']
  level5: IPageMeta['levels']['level_5_eng']
  level6: IPageMeta['levels']['level_6_eng']
  tag: string
  view: string
  reshet: string
  environment: string
  bundleid: string
}

/** why a VAST request brought no ad to play */
export type VastErrorType = 'no_fill' | 'timeout' | 'vast_error'

/** the IAB VAST error codes the ads report */
export const VAST_ERROR = {
  /** a wrapper (or the tag) did not answer in time */
  wrapperTimeout: '301',
  noAds: '303',
  /** a general linear error: the browser would not play it */
  linear: '400',
  /** the file did not come in time */
  mediaTimeout: '402',
  mediaNotSupported: '403',
  undefined: '900',
} as const

export type IVastResult =
  | {
      hasAds: true
      data: {
        url: string
        duration: number
        video: string
        vastAd: Record<string, unknown>
        vastCreative: Record<string, unknown>
        /** GAM's own ids when it redirected to another ad server */
        ids?: { lineItemId: string; creativeId: string }
      }
      error: null
    }
  | {
      hasAds: false
      data?: undefined
      error: string
      errorType: VastErrorType
      /** the VAST error code that stands for it */
      errorCode: string
    }

export const dfpUtils = (() => {
  return {
    DFPParams: () => {
      return {
        platform_key: 'platform',
        platform_value: !utils.isMobile()
          ? 'desktop'
          : playerConfig.inWebView
          ? 'webview_app'
          : 'mobile',
      }
    },

    urlToDfp: (url: string) => {
      const res = url.replace(/\//g, ' ').trim()
      return res === '' ? 'Hp' : res
    },

    geAssetKey: (country: string, videoRef: string) => {
      const ASEET_KEY = playerConfig.DFPParams.ASEET_KEY
      if (videoRef === 'stream_reshet_live1_token') {
        return ASEET_KEY['Token']
      } else {
        const location = country === 'Israel' ? 'Israel' : 'Global'
        const site_val = 'tv'
        const device = playerConfig.inWebView
          ? 'a'
          : !utils.isMobile()
          ? 'd'
          : 'm'
        return ASEET_KEY[location][site_val][device + '_key']
      }
    },

    getCustParams: ({
      pageType,
      levels,
      DFPType,
      pageViewedInDay,
      item,
      googlePal,
      referenceId,
      assetId,
      katuraId,
      environment = 'prod',
      componentId,
      ComponentType,
      platform,
      extraParams = [],
    }: {
      pageType: string
      levels: ILevels
      DFPType: string
      pageViewedInDay: number
      item: IItem | undefined
      googlePal?: string
      referenceId?: string | null
      assetId?: string | null
      katuraId?: string | null
      environment: string
      componentId?: string | number
      ComponentType?: 'mintly' | 'mini_series' | 'bigbrother' | string
      /** instead of the device's own value */
      platform?: string
      /** keys added at the end, their values encoded here */
      extraParams?: ReadonlyArray<readonly [string, string]>
    }) => {
      const DFP_PARAMS = dfpUtils.DFPParams()
      let tagVal = ''
      if (item !== undefined) {
        tagVal = utils.getParamsString(item.tags)
        tagVal = tagVal.replace(/,/g, '%26')
      }
      const adUnitObj = {
        url: window.location.pathname,
        environment: environment,
        key: 'reshet',
      }

      const keyValuesDFP = [
        { key: adUnitObj.key, value: dfpUtils.urlToDfp(adUnitObj.url) },
        {
          key: DFP_PARAMS.platform_key,
          value: platform ?? DFP_PARAMS.platform_value,
        },
        { key: 'environment', value: environment },
        { key: 'type_of_page', value: pageType },
        { key: 'bundleid', value: 'il.co.13tv' },
        { key: 'tag', value: tagVal },
      ]

      Object.keys(levels).forEach(key => {
        const match = key.match(/^level_(\d+)_eng$/)

        if (match && levels[key as keyof ILevels]) {
          const i = parseInt(match[1]) // This extracts the '4' (or any other number)
          if (i > 0) {
            const parsedLevel = {
              key: `level${i}`,
              value: levels[key as keyof ILevels] as string,
            }

            keyValuesDFP.push(parsedLevel)
          }
        }
      })

      if (componentId) {
        keyValuesDFP.push({
          key: 'component_id',
          value: componentId + '',
        })
      }

      if (ComponentType) {
        keyValuesDFP.push({
          key: 'Component_Type',
          value: ComponentType + '',
        })
      }
      const idxsegvalue = localStorage.getItem(
        'dmp-publisher-prevDefinitionsIds'
      )
      if (idxsegvalue) {
        keyValuesDFP.push({
          key: 'dxseg',
          value: idxsegvalue,
        })
      }
      const dmpid = localStorage.getItem('dmp-publisher-dmpid')
      if (dmpid) {
        keyValuesDFP.push({
          key: 'dxu',
          value: dmpid,
        })
      }
      if (googlePal) {
        keyValuesDFP.push({
          key: 'pal',
          value: googlePal,
        })
      }

      if (
        pageViewedInDay &&
        pageViewedInDay <
          playerConfig.fakeWindow.siteConfig.adds.dayViewMaxCount
      ) {
        keyValuesDFP.push({ key: 'VIEW', value: 'view' + pageViewedInDay })
      }
      if (referenceId) {
        keyValuesDFP.push({ key: 'ref', value: referenceId })
      }
      if (assetId) {
        keyValuesDFP.push({ key: 'assetId', value: assetId })
      }
      if (katuraId) {
        keyValuesDFP.push({ key: 'katuraId', value: katuraId })
      }
      // a value is encoded once for cust_params, and once more when the string
      // below is built in its URL form ('%3D', '%26')
      extraParams.forEach(([key, value]) => {
        const once = encodeURIComponent(value)
        keyValuesDFP.push({
          key,
          value: DFPType === 'midrollLive' ? once : encodeURIComponent(once),
        })
      })

      const length = keyValuesDFP.length
      let dfpstr = '',
        keyVal,
        val
      for (const param in keyValuesDFP) {
        // eslint-disable-next-line no-prototype-builtins
        if (keyValuesDFP.hasOwnProperty(param)) {
          keyVal = keyValuesDFP[param].key
          val = keyValuesDFP[param].value

          let equal = '%3D',
            ampersand = '%26',
            comma = '%2C'

          if (DFPType === 'midrollLive') {
            equal = '='
            ampersand = '&'
            comma = ','
          }

          if (keyVal === 'permutive' && val) {
            val = val.replace(/[[\]]+/g, '')
            val = val.replace(/,/g, comma)
          }

          dfpstr =
            dfpstr +
            keyVal +
            equal +
            val +
            (length === +param + 1 ? '' : ampersand)
        }
      }
      return dfpstr
    },
    buildVmapUrl: (baseUrl: string, custParams: string): string =>
      `${baseUrl}&cust_params=${custParams}`,

    getAdVideo: ({
      pagetypeMeta,
      pageType,
      adType,
      levels,
      pageViewedInDay,
      item,
      googlePal,
      referenceId,
      assetId,
      katuraId,
      environment = 'prod',
      videoDurationSec,
    }: {
      pagetypeMeta: string
      pageType: string
      adType: string
      levels: ILevels
      pageViewedInDay: number
      item?: IItem
      googlePal?: string
      referenceId: string | null
      katuraId?: string | null
      assetId?: string | null
      environment: string
      videoDurationSec?: number
    }) => {
      const ADS_VIDEO = playerConfig.DFPParams.ADS_VIDEO.tv
      const device = playerConfig.inWebView
        ? 'a'
        : !utils.isMobile()
        ? 'd'
        : 'm'
      const ad = adType ? '_' + adType : ''
      const VMAPStr = device + '_' + pageType + ad
      const custParams = dfpUtils.getCustParams({
        pageType: pagetypeMeta,
        levels,
        DFPType: adType,
        pageViewedInDay,
        item,
        googlePal,
        referenceId,
        assetId,
        katuraId,
        environment,
      })

      return dfpUtils.buildVmapUrl(
        ADS_VIDEO(VMAPStr, videoDurationSec),
        custParams
      )
    },

    AdVidDur: (duration: number) =>
      'video_' + Math.floor(duration / (60 * 3)) * 3,

    parseAdsIntoCollection: (vmapUrl: string) => {
      const parser = new DOMParser()
      const ads: AdS[] = []

      return fetch(vmapUrl)
        .then(result => result.text())
        .then(data => {
          const xmlDoc = parser.parseFromString(data, 'application/xml')
          const prerollTags = xmlDoc.getElementsByTagName('Preroll')

          for (let i = 0; i < prerollTags.length; i += 2) {
            const adTag = prerollTags[i].getElementsByTagName('Ad')[0]
            if (adTag) {
              const adContent = adTag.textContent?.trim() || null
              adContent && ads.push({ preroll: { ad: adContent } })
            }
          }

          const midrollTags = xmlDoc.getElementsByTagName('Midroll')
          const offsetTime = parseInt(getParameterByName('vmap_offset_time'))
          for (let i = 0; i < midrollTags.length; i++) {
            const timeOffset = midrollTags[i].getAttribute('timeOffset')
            const adTag = midrollTags[i].getElementsByTagName('Ad')[0]
            if (adTag) {
              const adContent = adTag.textContent?.trim()
              adContent &&
                ads.push({
                  midroll: {
                    timeOffset: timeOffset
                      ? dfpUtils.timeStringToSeconds(
                          offsetTime && offsetTime > 0
                            ? '00:0' + offsetTime * (i + 1) + ':00'
                            : timeOffset
                        )
                      : null,
                    ad: adContent,
                  },
                })
            }
          }
          return ads
        })
        .catch(reason => {
          prettyLog('reason', reason)
          return []
        })
    },
    /**
     * RESH13-1138 - IMA pre-roll tag for a vertical video played on an item page.
     * Same unit family as the reels ads, but its own Vertical_*Item units.
     */
    getAdVerticalItem: ({
      pagetypeMeta,
      referenceId,
      levels,
      pageViewedInDay,
      item,
      environment,
      googlePal,
      katuraId,
      assetId,
    }: {
      pagetypeMeta: string
      referenceId: string
      levels: ILevels
      pageViewedInDay?: number | null
      item?: IItem
      environment: string
      googlePal?: string
      katuraId?: string | null
      assetId?: string | null
    }) => {
      const device = playerConfig.inWebView
        ? 'a'
        : !utils.isMobile()
        ? 'd'
        : 'm'
      const baseUrl = playerConfig.DFPParams.ADS_VIDEO.verticalItem(
        device + '_verticalItem'
      )

      return dfpUtils.buildVmapUrl(
        baseUrl,
        dfpUtils.getCustParams({
          pageType: pagetypeMeta,
          levels,
          DFPType: 'prerol',
          referenceId,
          pageViewedInDay: pageViewedInDay || 1,
          item,
          assetId,
          environment,
          googlePal,
          katuraId,
        })
      )
    },
    parseVast: async ({
      referenceId,
      componentId,
      pageViewedInDay,
      item,
      ComponentType,
      assetId,
      environment,
      googlePal,
      katuraId,
      levels,
      adUnit,
      pageType = 'hp',
      platform,
      extraCustParams,
      timeoutMs,
    }: {
      referenceId: string
      pageViewedInDay?: number | null
      environment: string
      componentId?: string | number
      item?: IItem
      ComponentType?: 'mintly' | 'mini_series' | 'bigbrother' | string
      assetId?: string | null
      googlePal?: string
      katuraId?: string | null
      levels: ILevels
      /** another unit than the reels' (13 Shorts: SHORTS_AD_UNITS) */
      adUnit?: string
      pageType?: string
      platform?: string
      extraCustParams?: ReadonlyArray<readonly [string, string]>
      /** how long each VAST hop may take (the Shorts config) */
      timeoutMs?: number
    }): Promise<IVastResult> => {
      try {
        const ADS_VIDEO = playerConfig.DFPParams.ADS_VIDEO
        const site_val = 'reels'
        const device = playerConfig.inWebView
          ? 'a'
          : !utils.isMobile()
          ? 'd'
          : 'm'
        const VMAPStr = device + '_' + 'reels'
        const debug = getParameterByName('debugDFP') === 'yes'
        const baseUrl = !debug
          ? adUnit
            ? ADS_VIDEO.shorts(adUnit)
            : ADS_VIDEO[site_val](VMAPStr)
          : device === 'd'
          ? '/static/ad_reels_desktop.xml'
          : device === 'm'
          ? '/static/ad_reels_mobile.xml'
          : '/static/ad_reels_app.xml'
        const fetchUrl = debug
          ? baseUrl
          : dfpUtils.buildVmapUrl(
              baseUrl,
              dfpUtils.getCustParams({
                pageType,
                levels,
                DFPType: 'prerol',
                referenceId,
                pageViewedInDay: pageViewedInDay || 1,
                item,
                componentId,
                ComponentType,
                assetId,
                environment,
                googlePal,
                katuraId,
                platform,
                extraParams: extraCustParams,
              })
            )

        // the Shorts ask on a client of their own, which also tells why a
        // wrapper failed and which ids GAM gave a redirect
        const asked = adUnit
          ? await fetchVastWithDetails(fetchUrl, timeoutMs)
          : { response: await fetchAndParseVast(fetchUrl), details: null }
        const vastResponse = asked.response
        const ad = (vastResponse?.ads?.find(
          (a: VastParsedAd) => !a.hasFailed
        ) ?? vastResponse?.ads?.[0]) as VastParsedAd | undefined

        if (!ad) {
          const code = asked.details?.errorCode
          const failed = !!code && String(code) !== VAST_ERROR.noAds
          return {
            hasAds: false,
            error: 'No ads found',
            errorType: failed
              ? String(code) === VAST_ERROR.wrapperTimeout
                ? 'timeout'
                : 'vast_error'
              : 'no_fill',
            errorCode: failed ? String(code) : VAST_ERROR.noAds,
          }
        }

        const linear = getLinearCreative(ad)
        if (!linear) {
          fireVastError(ad, '303')
          return {
            hasAds: false,
            error: 'No linear creative found',
            errorType: 'vast_error',
            errorCode: VAST_ERROR.noAds,
          }
        }

        const mediaFile = pickBestMediaFile(linear.mediaFiles)

        if (!mediaFile?.fileURL) {
          fireVastError(ad, '403')
          return {
            hasAds: false,
            error: 'No media file found',
            errorType: 'vast_error',
            errorCode: VAST_ERROR.mediaNotSupported,
          }
        }

        const duration =
          typeof linear.duration === 'number' ? linear.duration : 0
        const wrapper = asked.details?.wrapper

        return {
          hasAds: true,
          data: {
            url: getClickThroughUrl(linear),
            duration,
            video: mediaFile.fileURL,
            vastAd: ad as Record<string, unknown>,
            vastCreative: linear as Record<string, unknown>,
            ...(wrapper?.adId && {
              ids: {
                lineItemId: wrapper.adId,
                creativeId: wrapper.creativeId,
              },
            }),
          },
          error: null,
        }
      } catch (error) {
        console.error('Error parsing VAST:', error)
        // vast-client aborts a request that takes too long ("AbortError")
        const timedOut = /abort|timed out|\(408\)/i.test(String(error))
        return {
          hasAds: false,
          error: 'error',
          errorType: timedOut ? 'timeout' : 'vast_error',
          errorCode: timedOut
            ? VAST_ERROR.wrapperTimeout
            : VAST_ERROR.undefined,
        }
      }
    },

    timeStringToSeconds: (timeString: string) => {
      if (timeString) {
        const [hours, minutes, seconds] = timeString.split(':').map(Number)
        return hours * 3600 + minutes * 60 + seconds
      }
      return null
    },

    buildCustParams: ({
      pageType,
      levels,
      route,
      tags,
      adTargetEnv,
      pageViewInDay,
    }: {
      pageType: IPageMeta['pageType'] | undefined | string
      levels: IPageMeta['levels'] | undefined
      route?: string
      tags?: ITag[] | undefined
      adTargetEnv?: string
      pageViewInDay: number
    }) => {
      const cust_params = {
        bundleid: 'il.co.13tv',
      } as ICustParams
      cust_params.platform =
        getAdDeviceType() === 'desktop'
          ? 'desktop'
          : getAdDeviceType() === 'mobile'
          ? 'mobile'
          : 'webview_app'
      pageType && (cust_params.type_of_page = pageType)
      levels?.level_1_eng && (cust_params.level1 = levels.level_1_eng)
      levels?.level_2_eng && (cust_params.level2 = levels.level_2_eng)
      levels?.level_3_eng && (cust_params.level3 = levels.level_3_eng)
      levels?.level_4_eng && (cust_params.level4 = levels.level_4_eng)
      // levels?.level_5_eng && (cust_params.level5 = levels.level_5_eng)
      // levels?.level_6_eng && (cust_params.level6 = levels.level_6_eng)

      if (tags) {
        cust_params.tag = tags
          .filter(tag => tag)
          .map(tag => tag.name)
          .join(',')
      }

      if (pageViewInDay < 5) {
        cust_params.view = pageViewInDay.toString()
      }

      const routeItems = route
        ?.split('?')[0]
        .split('/')
        .filter(p => p !== '')
      if (routeItems && routeItems.length > 0) {
        cust_params.reshet = `${routeItems
          .slice(1, routeItems.length)
          .join(' ')}`
      }

      if (adTargetEnv) {
        cust_params.environment = adTargetEnv
      }

      const prevDefinitionsIds = localStorage.getItem(
        'dmp-publisher-prevDefinitionsIds'
      )
      if (prevDefinitionsIds) {
        cust_params.dxseg = prevDefinitionsIds
      }
      const dmpid = localStorage.getItem('dmp-publisher-dmpid')
      if (dmpid) {
        cust_params.dxu = dmpid
      }

      return cust_params
    },
    setAdTargeting: ({
      pageType,
      levels,
      route,
      tags,
      AD_TARGET_ENV,
      pageViewInDay,
    }: {
      pageType: IPageMeta['pageType'] | string | undefined
      levels: IPageMeta['levels'] | undefined
      route: string
      tags?: ITag[] | undefined
      AD_TARGET_ENV?: string
      pageViewInDay: number
    }) => {
      if (!window.googletag) {
        console.warn('googletag is not defined')
        return
      }

      const cust_params = dfpUtils.buildCustParams({
        pageType,
        levels,
        route,
        tags,
        adTargetEnv: AD_TARGET_ENV,
        pageViewInDay,
      })
      if (!cust_params) {
        return
      }

      window.googletag.cmd.push(() => {
        window.googletag.pubads().clearTargeting()

        Object.entries(cust_params).forEach(([key, value]) => {
          window.googletag.pubads().setTargeting(key, value)
        })
      })
    },
  }
})()
